import { useEffect, useState, type MouseEvent } from 'react';

const SECTIONS = [
  { id: 'strategy', label: 'Strategy' },
  { id: 'exits', label: 'Exits' },
  { id: 'sessions', label: 'Sessions' },
  { id: 'after-launch', label: 'In practice' },
  { id: 'faq', label: 'FAQ' },
  { id: 'setup', label: 'Setup' },
];

/**
 * A slim, sticky in-page navigation for the How It Works page.
 *
 * The page stacks three long blocks (strategy, trading sessions, setup), so
 * this bar lets visitors jump straight to any of them. It sits right below
 * the fixed site header and highlights whichever section is currently under
 * the header as you scroll.
 *
 * The links keep a real `#` href (so they still work with right-click,
 * modifier-clicks and without JS), but a plain left click is intercepted and
 * handled with `scrollIntoView`. That keeps the smooth scroll and, crucially,
 * avoids pushing a new browser-history entry on every section click, so the
 * back button still leaves the page in one press.
 */
export default function SectionNav() {
  /* Nothing is highlighted until the visitor has actually scrolled past
     the first section. Starting with no active item avoids lighting up
     "Strategy" while they are still reading the QuickFacts strip. */
  const [active, setActive] = useState<string | null>(null);

  useEffect(() => {
    const ids = SECTIONS.map((section) => section.id);

    const update = () => {
      /* A section becomes "current" once its top passes just under the header. */
      const threshold = 170;
      let current: string | null = null;
      ids.forEach((id) => {
        const el = document.getElementById(id);
        if (el && el.getBoundingClientRect().top <= threshold) {
          current = id;
        }
      });
      setActive(current);
    };

    update();
    window.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
    };
  }, []);

  const goToSection = (event: MouseEvent<HTMLAnchorElement>, id: string) => {
    /* Let the browser handle modifier clicks (open in new tab, etc.). */
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
      return;
    }
    const target = document.getElementById(id);
    if (!target) {
      return;
    }
    event.preventDefault();
    target.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div className="sticky top-[66px] md:top-[74px] z-40 border-b border-background-200/80 bg-background-50/95 backdrop-blur-md">
      <div className="w-full px-4 md:px-8">
        <nav
          className="max-w-shell mx-auto flex items-center gap-1 overflow-x-auto no-scrollbar py-2"
          aria-label="On this page"
        >
          <span className="hidden lg:flex items-center gap-2 pr-2 text-xs font-semibold uppercase tracking-[0.16em] text-foreground-400 whitespace-nowrap">
            <i className="ri-map-pin-2-line" aria-hidden="true"></i>
            On this page
          </span>
          {SECTIONS.map((section) => {
            const isActive = active === section.id;
            return (
              <a
                key={section.id}
                href={`#${section.id}`}
                onClick={(event) => goToSection(event, section.id)}
                aria-current={isActive ? 'location' : undefined}
                className={`whitespace-nowrap rounded-full px-3.5 py-1.5 text-xs font-semibold transition-colors cursor-pointer outline-none focus-visible:ring-2 focus-visible:ring-primary-400 focus-visible:ring-offset-2 focus-visible:ring-offset-background-50 ${
                  isActive
                    ? 'bg-primary-100 text-primary-700'
                    : 'text-foreground-600 hover:bg-background-100 hover:text-foreground-900'
                }`}
              >
                {section.label}
              </a>
            );
          })}
        </nav>
      </div>
    </div>
  );
}